/* eslint-disable max-lines */
import { Button, Spinner } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getFormattedSeconds } from '../audio-recorder/mini-audio-player/util';
import { ICON_BUTTON_CLASS } from './sop-card-utils';

interface AudioRowProps {
  src: string;
  onReplaceRequest?: () => void;
}

export function AudioRow({ src, onReplaceRequest }: AudioRowProps) {
  return (
    <div className="flex items-center gap-2">
      <AudioPreview src={src} className="min-w-0 flex-1" />
      {onReplaceRequest && (
        <Button
          variant="icon"
          onClick={onReplaceRequest}
          className={ICON_BUTTON_CLASS}
          title="Regenerate after editing the message"
        >
          <RotateCcw className="size-4" />
        </Button>
      )}
    </div>
  );
}

interface PreviewPlayerProps {
  isLoading: boolean;
  resolveAudio: () => Promise<{ url: string } | undefined>;
  disabled?: boolean;
}

export function PreviewPlayer(props: PreviewPlayerProps) {
  const { isLoading, resolveAudio, disabled } = props;
  const [src, setSrc] = useState('');

  const onPlay = async () => {
    const audio = await resolveAudio();
    setSrc(audio?.url ?? '');
  };

  if (!src) {
    return (
      <Button
        variant="icon"
        onClick={onPlay}
        disabled={disabled || isLoading}
        className="text-onlight-text-2 dark:!text-ondark-text-2 flex gap-2 self-start !p-0 text-xs hover:bg-transparent"
      >
        {isLoading ? <Spinner /> : <Play className="size-4" />}
        <span>{isLoading ? 'Generating audio…' : 'Play message'}</span>
      </Button>
    );
  }

  return <AudioRow src={src} onReplaceRequest={() => setSrc('')} />;
}

interface AudioPreviewProps {
  src: string;
  className?: string;
}

function AudioPreview({ src, className }: AudioPreviewProps) {
  const ref = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBroken, setIsBroken] = useState(false);
  const isProbing = useRef(false);

  useEffect(() => {
    setIsPlaying(false);
    setElapsed(0);
    setDuration(0);
    setIsBroken(false);
  }, [src]);

  const progress = duration > 0 ? Math.min(elapsed / duration, 1) : 0;

  const onToggle = () => {
    const audio = ref.current;
    if (!audio) {
      return;
    }
    if (!audio.paused) {
      audio.pause();
      return;
    }
    audio.play().catch(() => setIsBroken(true));
  };

  const seekable = () => {
    const audio = ref.current;
    return audio && Number.isFinite(audio.duration) && audio.duration > 0
      ? audio
      : undefined;
  };

  const onSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = seekable();
    if (!audio) {
      return;
    }
    const { left, width } = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - left) / width, 0), 1);
    audio.currentTime = ratio * audio.duration;
  };

  const onSeekKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: -5, ArrowRight: 5 }[event.key];
    const audio = step && seekable();
    if (!audio) {
      return;
    }
    event.preventDefault();
    audio.currentTime = Math.min(
      Math.max(audio.currentTime + step, 0),
      audio.duration
    );
  };

  return (
    <div className={clsx('flex items-center gap-3', className)}>
      <audio
        ref={ref}
        src={src}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setIsBroken(true)}
        onTimeUpdate={(e) => {
          if (!isProbing.current) {
            setElapsed(e.currentTarget.currentTime);
          }
        }}
        onDurationChange={(e) => {
          const audio = e.currentTarget;
          if (!Number.isFinite(audio.duration)) {
            return;
          }
          setDuration(audio.duration);
          if (isProbing.current) {
            isProbing.current = false;
            audio.currentTime = 0;
            setElapsed(0);
          }
        }}
        onLoadedMetadata={(e) => {
          const audio = e.currentTarget;
          if (!Number.isFinite(audio.duration)) {
            isProbing.current = true;
            audio.currentTime = Number.MAX_SAFE_INTEGER;
            return;
          }
          setDuration(audio.duration);
        }}
        className="hidden"
      />
      <Button
        variant="icon"
        onClick={onToggle}
        disabled={isBroken}
        title={isPlaying ? 'Pause' : 'Play'}
        className="text-onlight-text-1 dark:text-ondark-text-1 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3 shrink-0 !p-1"
      >
        {isPlaying ? <Pause className="size-5" /> : <Play className="size-5" />}
      </Button>
      <div
        onClick={onSeek}
        onKeyDown={onSeekKey}
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(elapsed)}
        aria-valuetext={`${getFormattedSeconds(
          elapsed
        )} of ${getFormattedSeconds(duration)}`}
        className="bg-onlight-line-3 dark:bg-ondark-line-2 focus-visible:ring-primary-500 h-1 min-w-0 flex-1 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2"
      >
        <div
          className="bg-primary-500 h-full rounded-full"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      <span
        className={clsx(
          'shrink-0 text-xs tabular-nums',
          isBroken
            ? 'text-alert-500'
            : 'text-onlight-text-2 dark:text-ondark-text-2'
        )}
      >
        {isBroken
          ? 'Cannot play this clip'
          : `${getFormattedSeconds(elapsed)} / ${getFormattedSeconds(
              duration
            )}`}
      </span>
    </div>
  );
}

export default AudioPreview;
